import { useTranslation } from "react-i18next"

import { Skeleton } from "@/components/ui/skeleton"
import { useIsMobile } from "@/hooks/use-mobile"

/**
 * OKS-25 F9 — Crew Roster loading skeleton.
 *
 * Mirrors the populated layout (3 stat-tile placeholders + 5 row × 6
 * column table) on desktop ≥640px; 5 stacked card placeholders on
 * mobile. Stats placeholders are conventional Card-shaped Skeletons
 * with the same `grid-cols-1 sm:grid-cols-3` layout as the populated
 * `<CrewQuickStats>`.
 *
 * Surfaces only during Inertia partial reloads (first paint comes from
 * the server; SSR'd HTML never shows the skeleton). Caller (page F10)
 * mounts this when `router.on("start")` fires for a same-page visit.
 *
 * Locked testid: `crew-list-loading` (per UX Spec §States — Loading).
 */
export function CrewListSkeleton() {
  const isMobile = useIsMobile()
  const { t } = useTranslation()
  const rows = Array.from({ length: 5 })

  return (
    <div
      data-testid="crew-list-loading"
      role="status"
      aria-label={t("crew.list.loading.label")}
      className="flex flex-col gap-4"
    >
      {/* 3-tile stat strip skeleton */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div
            key={i}
            className="border-border bg-card space-y-2 rounded-lg border p-5"
          >
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-8 w-16" />
          </div>
        ))}
      </div>

      {/* Table / card list skeleton */}
      {isMobile ? (
        <div className="flex flex-col gap-3">
          {rows.map((_, i) => (
            <div
              key={i}
              className="border-border bg-card flex items-center gap-3 rounded-lg border p-4"
            >
              <Skeleton className="size-10 rounded-full" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-3 w-32" />
                <Skeleton className="h-3 w-20" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-md border">
          <div className="grid grid-cols-6 gap-3 border-b p-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-3 w-full" />
            ))}
          </div>
          {rows.map((_, i) => (
            <div
              key={i}
              className="grid grid-cols-6 items-center gap-3 border-b p-3 last:border-b-0"
            >
              <div className="flex items-center gap-3">
                <Skeleton className="size-9 rounded-full" />
                <div className="flex-1 space-y-1.5">
                  <Skeleton className="h-3 w-24" />
                  <Skeleton className="h-2 w-16" />
                </div>
              </div>
              <Skeleton className="h-3 w-12" />
              <Skeleton className="h-3 w-10" />
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-3 w-8" />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
